"use client";

import * as React from "react";
import { ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  flattenAdminNavigation,
  isAdminNavigationPathActive,
  type AdminNavigationFolder,
  type AdminNavigationGroup,
  type AdminNavigationItem,
  type AdminNavigationLink,
} from "@/lib/adminNavigation";
import { usePathname } from "@/lib/router-compat";
import { cn } from "@/lib/utils";

const OPEN_FOLDER_IDS_STORAGE_KEY = "sumacomi:admin-sidebar:open-items:v2";
const OPEN_GROUP_ID_STORAGE_KEY = "sumacomi:admin-sidebar:open-group:v1";

function readStoredFolderIds(): Set<string> {
  if (typeof window === "undefined") return new Set();

  try {
    const stored = window.sessionStorage.getItem(OPEN_FOLDER_IDS_STORAGE_KEY);
    if (!stored) return new Set();
    const parsed: unknown = JSON.parse(stored);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((id): id is string => typeof id === "string"));
  } catch {
    return new Set();
  }
}

function writeStoredFolderIds(ids: Set<string>) {
  if (typeof window === "undefined") return;

  try {
    window.sessionStorage.setItem(
      OPEN_FOLDER_IDS_STORAGE_KEY,
      JSON.stringify([...ids])
    );
  } catch {
    // ストレージが利用できない場合も、現在の表示中はstateで維持する。
  }
}

function readStoredGroupId(): string | null {
  if (typeof window === "undefined") return null;

  try {
    return window.sessionStorage.getItem(OPEN_GROUP_ID_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStoredGroupId(id: string | null) {
  if (typeof window === "undefined") return;

  try {
    if (id) {
      window.sessionStorage.setItem(OPEN_GROUP_ID_STORAGE_KEY, id);
    } else {
      window.sessionStorage.removeItem(OPEN_GROUP_ID_STORAGE_KEY);
    }
  } catch {
    // ストレージが利用できない場合も、現在の表示中はstateで維持する。
  }
}

function findActiveLinkId(
  groups: readonly AdminNavigationGroup[],
  activePath: string
): string | null {
  return (
    flattenAdminNavigation(groups)
      .filter((destination) =>
        isAdminNavigationPathActive(activePath, destination.url)
      )
      .sort((a, b) => b.url.length - a.url.length)[0]?.id ?? null
  );
}

function findActiveGroupId(
  groups: readonly AdminNavigationGroup[],
  activeLinkId: string | null
): string | null {
  if (!activeLinkId) return null;

  return (
    groups.find((group) =>
      group.items.some((item) =>
        item.kind === "link"
          ? item.id === activeLinkId
          : item.items.some((link) => link.id === activeLinkId)
      )
    )?.id ?? null
  );
}

function findActiveFolder(
  groups: readonly AdminNavigationGroup[],
  activeLinkId: string | null
): { id: string; siblingIds: string[] } | null {
  if (!activeLinkId) return null;

  for (const group of groups) {
    const folders = group.items.filter(
      (item): item is AdminNavigationFolder => item.kind === "folder"
    );
    const activeFolder = folders.find((folder) =>
      folder.items.some((link) => link.id === activeLinkId)
    );
    if (activeFolder) {
      return {
        id: activeFolder.id,
        siblingIds: folders.map((folder) => folder.id),
      };
    }
  }

  return null;
}

function NavMainItem({
  item,
  activeLinkId,
  isOpen,
  siblingFolderIds,
  onOpenChange,
  onNavigate,
}: {
  item: AdminNavigationItem;
  activeLinkId: string | null;
  isOpen: boolean;
  siblingFolderIds: string[];
  onOpenChange: (
    itemId: string,
    open: boolean,
    siblingIds: readonly string[]
  ) => void;
  onNavigate: (url: string) => void;
}) {
  const { isMobile, setOpenMobile } = useSidebar();

  const navigate = (
    url: string,
    event?: React.MouseEvent<HTMLAnchorElement>
  ) => {
    if (
      event &&
      (event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey)
    ) {
      return;
    }

    onNavigate(url);
    if (isMobile) setOpenMobile(false);
  };

  if (item.kind === "link") {
    const Icon = item.icon;
    const active = activeLinkId === item.id;

    return (
      <SidebarMenuItem>
        <SidebarMenuButton
          asChild
          tooltip={item.title}
          isActive={active}
          className="min-h-10 font-medium md:min-h-8"
        >
          <Link
            to={item.url}
            aria-current={active ? "page" : undefined}
            onClick={(event) => navigate(item.url, event)}
          >
            {Icon ? <Icon aria-hidden="true" /> : null}
            <span>{item.title}</span>
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }

  const isActive = item.items.some((link) => link.id === activeLinkId);
  const submenuId = `admin-navigation-${item.id}`;
  const FolderIcon = item.icon;

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        type="button"
        aria-expanded={isOpen}
        aria-controls={submenuId}
        isActive={isActive && !isOpen}
        className="min-h-10 cursor-pointer font-medium md:min-h-8"
        onClick={() => onOpenChange(item.id, !isOpen, siblingFolderIds)}
      >
        <FolderIcon aria-hidden="true" />
        <span>{item.title}</span>
        <ChevronRight
          className={`ml-auto transition-transform duration-200 motion-reduce:transition-none ${
            isOpen ? "rotate-90" : ""
          }`}
          aria-hidden="true"
        />
      </SidebarMenuButton>
      {isOpen ? (
        <div id={submenuId}>
          <SidebarMenuSub>
            {item.items.map((link: AdminNavigationLink) => {
              const active = activeLinkId === link.id;

              return (
                <SidebarMenuSubItem key={link.id}>
                  <SidebarMenuSubButton
                    asChild
                    isActive={active}
                    className={cn(
                      "relative min-h-10 overflow-hidden font-normal md:min-h-7",
                      active &&
                        "bg-sidebar-primary text-sidebar-primary-foreground hover:bg-sidebar-primary/90 hover:text-sidebar-primary-foreground font-semibold"
                    )}
                  >
                    <Link
                      to={link.url}
                      aria-current={active ? "page" : undefined}
                      onClick={(event) => navigate(link.url, event)}
                    >
                      <span className="relative z-10">{link.title}</span>
                    </Link>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              );
            })}
          </SidebarMenuSub>
        </div>
      ) : null}
    </SidebarMenuItem>
  );
}

function CollapsedGroup({
  group,
  activeLinkId,
  onNavigate,
}: {
  group: AdminNavigationGroup;
  activeLinkId: string | null;
  onNavigate: (url: string) => void;
}) {
  const { isMobile, setOpenMobile } = useSidebar();
  const [open, setOpen] = React.useState(false);
  const destinations = React.useMemo(
    () => flattenAdminNavigation([group]),
    [group]
  );
  const active = destinations.some(
    (destination) => destination.id === activeLinkId
  );
  const GroupIcon = group.icon;

  const navigate = (
    url: string,
    event: React.MouseEvent<HTMLAnchorElement>
  ) => {
    if (
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    onNavigate(url);
    setOpen(false);
    if (isMobile) setOpenMobile(false);
  };

  return (
    <SidebarMenuItem>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <SidebarMenuButton
            type="button"
            tooltip={group.label}
            aria-label={group.label}
            isActive={active}
            className="min-h-10 cursor-pointer md:min-h-8"
          >
            <GroupIcon aria-hidden="true" />
          </SidebarMenuButton>
        </PopoverTrigger>
        <PopoverContent side="right" align="start" className="w-72 p-1">
          <p className="text-muted-foreground px-2 pb-1 pt-1 text-xs font-semibold">
            {group.label}
          </p>
          <ul aria-label={group.label}>
            {destinations.map((destination) => {
              const destinationActive = destination.id === activeLinkId;
              return (
                <li key={destination.id}>
                  <Link
                    to={destination.url}
                    aria-current={destinationActive ? "page" : undefined}
                    onClick={(event) => navigate(destination.url, event)}
                    className={`focus-visible:ring-sidebar-ring flex min-h-11 w-full items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 ${
                      destinationActive
                        ? "bg-sidebar-primary text-sidebar-primary-foreground font-semibold"
                        : "text-foreground hover:bg-sidebar-accent/70"
                    }`}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">
                        {destination.title}
                      </span>
                      {destination.parentTitle ? (
                        <span className="text-muted-foreground block truncate text-xs">
                          {destination.parentTitle}
                        </span>
                      ) : null}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </PopoverContent>
      </Popover>
    </SidebarMenuItem>
  );
}

export function NavMain({
  groups,
}: {
  groups: readonly AdminNavigationGroup[];
}) {
  const pathname = usePathname();
  const { state: sidebarState } = useSidebar();
  const [pendingPath, setPendingPath] = React.useState<string | null>(null);
  const activePath = pendingPath ?? pathname;
  const activeLinkId = React.useMemo(
    () => findActiveLinkId(groups, activePath),
    [activePath, groups]
  );
  const activeGroupId = React.useMemo(
    () => findActiveGroupId(groups, activeLinkId),
    [activeLinkId, groups]
  );
  const activeFolder = React.useMemo(
    () => findActiveFolder(groups, activeLinkId),
    [activeLinkId, groups]
  );
  const [openGroupId, setOpenGroupId] = React.useState<string | null>(() => {
    const stored = readStoredGroupId();
    return (
      activeGroupId ??
      groups.find((group) => group.id === stored)?.id ??
      groups[0]?.id ??
      null
    );
  });
  const [openFolderIds, setOpenFolderIds] = React.useState<Set<string>>(() => {
    const initialIds = readStoredFolderIds();
    if (activeFolder) initialIds.add(activeFolder.id);
    return initialIds;
  });
  const isCollapsed = sidebarState === "collapsed";

  React.useEffect(() => {
    setPendingPath(null);
  }, [pathname]);

  React.useEffect(() => {
    if (activeGroupId) setOpenGroupId(activeGroupId);
  }, [activeGroupId]);

  React.useEffect(() => {
    if (!activeFolder) return;

    setOpenFolderIds((current) => {
      if (
        current.has(activeFolder.id) &&
        !activeFolder.siblingIds.some(
          (id) => id !== activeFolder.id && current.has(id)
        )
      ) {
        return current;
      }

      const next = new Set(current);
      activeFolder.siblingIds.forEach((id) => next.delete(id));
      next.add(activeFolder.id);
      return next;
    });
  }, [activeFolder]);

  React.useEffect(() => {
    writeStoredGroupId(openGroupId);
  }, [openGroupId]);

  React.useEffect(() => {
    writeStoredFolderIds(openFolderIds);
  }, [openFolderIds]);

  const handleFolderOpenChange = React.useCallback(
    (itemId: string, open: boolean, siblingIds: readonly string[]) => {
      setOpenFolderIds((current) => {
        const next = new Set(current);
        if (open) {
          siblingIds.forEach((id) => next.delete(id));
          next.add(itemId);
        } else {
          next.delete(itemId);
        }
        return next;
      });
    },
    []
  );

  if (isCollapsed) {
    return (
      <nav aria-label="メインメニュー">
        <SidebarGroup className="px-1 pt-1">
          <SidebarMenu initial={false}>
            {groups.map((group) => (
              <CollapsedGroup
                key={group.id}
                group={group}
                activeLinkId={activeLinkId}
                onNavigate={setPendingPath}
              />
            ))}
          </SidebarMenu>
        </SidebarGroup>
      </nav>
    );
  }

  return (
    <nav aria-label="メインメニュー">
      <SidebarGroup className="pt-1">
        <SidebarGroupLabel>業務メニュー</SidebarGroupLabel>
        <SidebarMenu initial={false}>
          {groups.map((group) => {
            const open = openGroupId === group.id;
            const groupActive = activeGroupId === group.id;
            const panelId = `admin-navigation-group-${group.id}`;
            const folderIds = group.items
              .filter(
                (item): item is AdminNavigationFolder => item.kind === "folder"
              )
              .map((item) => item.id);
            const GroupIcon = group.icon;

            return (
              <SidebarMenuItem key={group.id}>
                <SidebarMenuButton
                  type="button"
                  aria-expanded={open}
                  aria-controls={panelId}
                  isActive={groupActive && !open}
                  onClick={() => setOpenGroupId(open ? null : group.id)}
                  className="min-h-10 cursor-pointer font-medium md:min-h-9"
                >
                  <GroupIcon aria-hidden="true" />
                  <span>{group.label}</span>
                  <ChevronRight
                    className={`ml-auto transition-transform duration-200 motion-reduce:transition-none ${
                      open ? "rotate-90" : ""
                    }`}
                    aria-hidden="true"
                  />
                </SidebarMenuButton>
                {open ? (
                  <div
                    id={panelId}
                    className="border-sidebar-border ml-3 border-l pl-1"
                  >
                    <SidebarMenu initial={false}>
                      {group.items.map((item) => (
                        <NavMainItem
                          key={item.id}
                          item={item}
                          activeLinkId={activeLinkId}
                          isOpen={openFolderIds.has(item.id)}
                          siblingFolderIds={folderIds}
                          onOpenChange={handleFolderOpenChange}
                          onNavigate={setPendingPath}
                        />
                      ))}
                    </SidebarMenu>
                  </div>
                ) : null}
              </SidebarMenuItem>
            );
          })}
        </SidebarMenu>
      </SidebarGroup>
    </nav>
  );
}
